import { IBtnAction } from "dots-ui/dotsModal";
import { FormikProps } from "formik";
import { useContext, useMemo, useState } from "react";
import { AvisConstants } from "../../../../../../data/avisConstants";
import {
  ItemViewModel,
  LicenseCreationInfo,
  ProgramReportRow,
  SubmitProgramReport,
} from "../../../../../../generated/data-contracts";
import { AvisUtils } from "../../../../../../utils";
import {
  AvisEditableList,
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import { AvisSection } from "../../../../../shared/components/form";
import {
  AvisInputInt,
  AvisInputMaskTiming,
} from "../../../../../shared/components/inputs";
import { AvisModal } from "../../../../../shared/components/modal";
import { AvisContext } from "../../../../../shared/context/AvisContext";
import { CreationsPicker } from "../modalForms/creationsPicker/CreationsPicker";
import parse from "html-react-parser";

interface IProgramReportIdentifiedCreationsProps {
  bag: FormikProps<SubmitProgramReport>;
  bagField: keyof SubmitProgramReport;
  creationClassesPublic: ItemViewModel[];
  disabled: boolean;
  loading: boolean;
}
export function ProgramReportIdentifiedCreations(
  props: Readonly<IProgramReportIdentifiedCreationsProps>,
) {
  const { bag, bagField, creationClassesPublic, disabled, loading } = props;
  const { res, ctxPortalSettings } = useContext(AvisContext);
  const [isModalOpen, setIsModalOpen] = useState<boolean>();
  const [isModalViewSelectedOpen, setIsModalViewSelectedOpen] =
    useState<boolean>(false);

  const [selectedCreations, setSelectedCreations] = useState<
    LicenseCreationInfo[]
  >([]);

  const handleModalConfirm = () => {
    if (!bag?.values?.programReportRows) return;
    setIsModalOpen(false);
    //Add selected creations to bag of ProgramReportRows
    const newRows: ProgramReportRow[] = [];
    selectedCreations.forEach((item) => {
      newRows.push({
        creationClassId: item.creationClassId,
        creationId: item.creationId,
        creationInfo: item,
        playCount: 1, //default value
        playLength: ctxPortalSettings?.defaultCreationTiming,
        rowState: "Add",
      } as IRowState<ProgramReportRow>);
    });
    bag.values.programReportRows = (
      bag.values[bagField] as IRowState<ProgramReportRow>[]
    ).concat(newRows);
    bag.setValues(bag.values);
    setSelectedCreations([]);
    setIsModalViewSelectedOpen(false);
    AvisUtils.sessionDataCleanupFilterData(
      AvisConstants.licenses_creations_filter_values_key,
    );
  };
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCreations([]);
    setIsModalViewSelectedOpen(false);
    AvisUtils.sessionDataCleanupFilterData(
      AvisConstants.licenses_creations_filter_values_key,
    );
  };
  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<ProgramReportRow>
  >[] = useMemo(
    () => [
      {
        name: "creationClassId",
        title: res("label_CreationClassName"),
        minWidth: 120,
        width: 120,
        forceViewMode: true,
        transformViewValue: (_fieldValue, rowValue) => {
          return rowValue.creationInfo?.creationClassName ?? "";
        },
      },
      {
        name: "creationInfo",
        title: res("label_Title"),
        minWidth: 150,
        width: "50%",
        forceViewMode: true,
        transformViewValue: (_fieldValue, rowValue) => {
          return rowValue.creationInfo?.creationNameToDisplay ?? "";
        },
      },
      {
        name: "creationId",
        title: res("licensees_LicenseReports_PP_label_Authors"),
        minWidth: 150,
        width: "50%",
        forceViewMode: true,
        transformViewValue: (_fieldValue, rowValue) => {
          const rowValueString =
            rowValue.creationInfo?.rightsHolderNamesProgramReportForm ?? "";
          const parsedValue = parse(rowValueString);
          return parsedValue;
        },
      },
      {
        name: "playLength",
        title: res("label_PlayLength"),
        width: 80,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputMaskTiming
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              disabled={disabled}
              hideClear
              onChange={onChange}
            />
          );
        },
      },
      {
        name: "playCount",
        title: res("label_PlayCount"),
        width: 75,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              min={1}
              disabled={disabled}
              hideClear
            />
          );
        },
      },
    ],
    [disabled, res],
  );
  const isModalViewSelectedValue = useMemo(
    () => isModalViewSelectedOpen && selectedCreations.length > 0,
    [isModalViewSelectedOpen, selectedCreations.length],
  );
  const additionalDialogButtons = useMemo(() => {
    if (selectedCreations.length === 0) return [] as IBtnAction[];

    return [
      {
        action: () => {
          setIsModalViewSelectedOpen((prev) => !prev);
        },
        text: isModalViewSelectedValue
          ? res(
              "licensees_LicenseReports_PP_section_Identified_Modal_ContinueSearch",
            )
          : res(
              "licensees_LicenseReports_PP_section_Identified_Modal_PreviewSelected",
            ),
        className: "primary btn-flex-left",
      },
    ];
  }, [isModalViewSelectedValue, res, selectedCreations.length]);

  return (
    <AvisSection
      title={res("licensees_LicenseReports_PP_section_Identified")}
      textToDisplayKey="portal_LIC_ProgramReport_1"
    >
      <AvisEditableList<SubmitProgramReport, IRowState<ProgramReportRow>>
        bag={bag}
        bagField={bagField}
        initValue={{
          playLength: ctxPortalSettings?.defaultCreationTiming,
          creationClassId: 0,
          creationId: 0,
        }}
        fields={fieldCollection}
        disabled={disabled}
        loading={loading}
        customAddEditConfig={{
          customSetModalOpen: setIsModalOpen,
        }}
      />
      {isModalOpen && (
        <AvisModal
          isOpen={isModalOpen}
          title={res("licensees_LicenseReports_PP_section_Identified_Modal")}
          confirmButtonDisabled={selectedCreations.length === 0}
          confirmButtonTextSuffix={
            selectedCreations.length > 0 ? `(${selectedCreations.length})` : ""
          }
          onConfirm={handleModalConfirm}
          onClose={handleModalClose}
          additionalActions={additionalDialogButtons}
        >
          <CreationsPicker
            isViewSelectedView={isModalViewSelectedValue}
            selectedPublicCreations={selectedCreations}
            setSelectedPublicCreations={setSelectedCreations}
            setIsViewSelectedView={setIsModalViewSelectedOpen}
            creationClassesPublic={creationClassesPublic}
            selectedReadOnly={(
              bag.values[bagField] as IRowState<ProgramReportRow>[]
            ).map((item) => item.creationId)}
          />
        </AvisModal>
      )}
    </AvisSection>
  );
}
